// Línea de tiempo de una nota: SOLO hechos que el backend registró (ventas.transicion de la nota y de
// cada despacho, decisiones de reserva, eventos de estado del DTE). No se inventa nada: una
// previsualización no es un hecho (no se guarda), así que no aparece; una revisión guardada, sí.
import {
  useDespachosDeNota,
  useDetallesDespacho,
  useDocumentacion,
  useDocumentoDte,
} from '../api/consultas';
import type { Nota, Transicion } from '../api/contratos';
import { decimal, fechaHora } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { useNombreActor } from '../ventas/Paneles';
import { nombreTipoDte } from '../dte/EstadoDte';

interface Hecho {
  en: string;
  etapa: 'NOTA' | 'RESERVA' | 'DESPACHO' | 'DTE';
  titulo: string;
  detalle?: string | undefined;
  actor?: string | null | undefined;
}

const ACCION_NOTA: Record<string, string> = {
  crear: 'CREADA',
  editar: 'REVISIÓN GUARDADA',
  confirmar: 'CONFIRMADA',
  enviar_aprobacion: 'ENVIADA A APROBACIÓN',
  aprobar: 'APROBADA',
  rechazar: 'RECHAZADA',
  retirar_aprobacion: 'APROBACIÓN RETIRADA',
  descartar: 'DESCARTADA',
  revisar: 'REVISADA',
  cerrar_corta: 'CERRADA CORTA',
  anular: 'ANULADA',
};
const ACCION_DESPACHO: Record<string, string> = {
  crear: 'PREPARADO',
  editar: 'EDITADO',
  confirmar: 'DESPACHADO (salida confirmada)',
  descartar: 'DESCARTADO',
};
const DIMENSION_DTE: Record<string, string> = {
  documento: 'documento',
  generacion: 'generación',
  firma: 'firma',
  envio: 'envío',
  sii: 'SII',
};

/** Los eventos de estado de UN DTE (su historial en el backend). */
function useHechosDte(empresaId: string, documentoIds: string[], habilitado: boolean) {
  // Pocos documentos por nota: una lectura por documento.
  const a = useDocumentoDte(empresaId, documentoIds[0] ?? null, habilitado);
  const b = useDocumentoDte(empresaId, documentoIds[1] ?? null, habilitado);
  const c = useDocumentoDte(empresaId, documentoIds[2] ?? null, habilitado);
  return [a, b, c].flatMap((q) =>
    (q.data?.historial ?? []).map((h): Hecho => ({
      en: h.ocurrido_en,
      etapa: 'DTE',
      titulo: `DTE ${nombreTipoDte(q.data!.tipoDte)} · ${DIMENSION_DTE[h.dimension] ?? h.dimension}: ${h.valor}`,
      detalle: [q.data!.folio ? `folio ${q.data!.folio}` : null, h.codigo_externo]
        .filter(Boolean)
        .join(' · '),
    })),
  );
}

export function LineaDeTiempo({ nota }: { nota: Nota }) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const actor = useNombreActor(nota.nombres);
  const confirmada = ['CONFIRMADA', 'CERRADA_CORTA'].includes(nota.estado);
  const despachos = useDespachosDeNota(empresaId, confirmada ? nota.notaId : null);
  const ids = (despachos.data?.items ?? []).map((d) => d.id);
  const detalles = useDetallesDespacho(empresaId, ids);
  const verDte = confirmada && puede('dte.ver');
  const doc = useDocumentacion(empresaId, nota.notaId, verDte);
  const dteIds = (doc.data?.documentos ?? []).flatMap((d) => (d.dte ? [d.dte.documentoId] : []));
  const hechosDte = useHechosDte(empresaId, dteIds, verDte);

  const hechos: Hecho[] = [
    ...nota.historia.map((h: Transicion): Hecho => ({
      en: h.en,
      etapa: 'NOTA',
      titulo: `${ACCION_NOTA[h.accion] ?? h.accion.toUpperCase()}${h.accion === 'editar' ? ` (rev. ${h.revision})` : ''}`,
      detalle: h.motivo ?? undefined,
      actor: h.actor_usuario_id,
    })),
    ...nota.reservas.decisiones.map((d): Hecho => ({
      en: d.decidido_en,
      etapa: 'RESERVA',
      titulo: 'RESERVADA',
      detalle: `solicitado ${decimal(d.requerido)} · reservado ${decimal(d.reservado)} · pendiente ${decimal(d.pendiente)} (u. base)`,
      actor: d.decidido_por,
    })),
    ...detalles.flatMap((q) =>
      (q.data?.historia ?? []).map((h): Hecho => ({
        en: h.en,
        etapa: 'DESPACHO',
        titulo: `DESPACHO #${q.data!.numero} ${ACCION_DESPACHO[h.accion] ?? h.accion.toUpperCase()}`,
        detalle: (q.data!.lineas ?? [])
          .map((l) => `L${l.numero}: ${decimal(l.cantidad_base)}`)
          .join(' · '),
        actor: h.actor_usuario_id,
      })),
    ),
    ...hechosDte,
  ].sort((x, y) => x.en.localeCompare(y.en));

  return (
    <section className="panel" aria-label="Línea de tiempo">
      <h3>Línea de tiempo</h3>
      <ol className="linea-tiempo">
        {hechos.map((h, i) => (
          <li key={`${h.en}-${i}`} data-etapa={h.etapa} data-hecho={h.titulo}>
            <span className="lt-fecha mono">{fechaHora(h.en)}</span>
            <span className={`lt-etapa etapa-${h.etapa.toLowerCase()}`}>{h.etapa}</span>
            <strong>{h.titulo}</strong>
            {h.detalle && <span className="apagado"> · {h.detalle}</span>}
            {h.actor !== undefined && <span className="apagado"> · {actor(h.actor)}</span>}
          </li>
        ))}
      </ol>
      {!verDte && confirmada && (
        <p className="ayuda">Los eventos del documento tributario requieren permiso de ver DTE.</p>
      )}
    </section>
  );
}
